import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, CaretLeft, Plus, Trash, Power, EnvelopeSimple } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const inputCls = "h-10 bg-transparent border-white/10 text-white text-sm placeholder:text-white/25";
const lbl = "text-[10px] tracking-[0.15em] uppercase text-white/50";
const STATUS_LABEL = { pending: "in attesa", sent: "inviata", failed: "fallita", cancelled: "annullata" };
const STATUS_COLOR = { pending: "text-amber-300", sent: "text-emerald-400", failed: "text-red-400", cancelled: "text-white/35" };

export default function MarketingAutomationsPage() {
  const [autos, setAutos] = useState([]);
  const [lists, setLists] = useState([]);
  const [queue, setQueue] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", list_id: "", steps: [] });

  const load = () => {
    api.get("/admin/marketing/automations").then(({ data }) => setAutos(data)).catch(() => {});
    api.get("/admin/marketing/automations/queue").then(({ data }) => setQueue(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/admin/marketing/lists").then(({ data }) => setLists(data)).catch(() => {});
  }, []);

  const openNew = () => {
    setForm({ name: "", list_id: "", steps: [{ delay_days: 0, subject: "", html: "" }] });
    setEditing({ id: null });
  };
  const openEdit = (a) => {
    setForm({ name: a.name, list_id: a.list_id, steps: a.steps.map((s) => ({ ...s })) });
    setEditing(a);
  };
  const setStep = (i, patch) => setForm((f) => ({ ...f, steps: f.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/marketing/automations", {
        id: editing?.id || null, name: form.name, list_id: form.list_id, active: editing?.active ?? true,
        steps: form.steps.map((s) => ({ delay_days: Number(s.delay_days) || 0, subject: s.subject, html: s.html })),
      });
      toast.success(editing?.id ? "Sequenza aggiornata" : "Sequenza creata: si applica ai nuovi contatti aggiunti alla lista da ora in poi");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (a) => {
    try {
      const { data } = await api.put(`/admin/marketing/automations/${a.id}/toggle`);
      toast.success(data.active ? "Sequenza attivata" : "Sequenza sospesa");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDelete = async (a) => {
    try {
      await api.delete(`/admin/marketing/automations/${a.id}`);
      toast.success("Sequenza eliminata (invii in attesa annullati)");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-automations-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 animate-fade-up">
        <div>
          <Link to="/marketing/email" data-testid="back-to-email" className="inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white transition-colors mb-4">
            <CaretLeft size={13} /> Email
          </Link>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2 flex items-center gap-1.5">
            <Lightning size={13} className="text-amber-400" /> Sequenze automatiche
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Automazioni</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Quando un nuovo contatto entra in una lista (a mano, da CSV o da webapp), riceve automaticamente le email della sequenza ai tempi impostati. Invio tramite SMTP EmailMarketing.
          </p>
        </div>
        <Button data-testid="new-automation-button" onClick={openNew}
          className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
          <Plus size={16} className="mr-1.5" /> Nuova sequenza
        </Button>
      </div>

      {autos.length === 0 ? (
        <div className="p-10 rounded-lg bg-surface border border-surface-border text-center animate-fade-up" data-testid="automations-empty">
          <Lightning size={40} weight="duotone" className="text-white/20 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Nessuna sequenza ancora. Esempio: "Benvenuto" — email subito + promemoria dopo 3 giorni.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="automations-grid">
          {autos.map((a, i) => (
            <div key={a.id} className="p-6 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid={`automation-card-${a.id}`} style={{ animationDelay: `${i * 60}ms` }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-heading text-xl font-semibold tracking-tight truncate">{a.name}</h3>
                  <p className="text-xs text-slate-400 mt-1">Lista: <span className="text-white/80">{a.list_name}</span> — {a.steps.length} email — <span className="text-emerald-400">{a.sent_count} inviate</span></p>
                </div>
                <span className={`inline-flex items-center h-6 px-3 rounded-full text-[10px] font-semibold uppercase tracking-wider border shrink-0 ${a.active ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-400" : "bg-white/5 border-white/10 text-white/40"}`} data-testid={`automation-status-${a.id}`}>
                  {a.active ? "Attiva" : "Sospesa"}
                </span>
              </div>
              <div className="mt-3 space-y-1">
                {a.steps.map((s, j) => (
                  <p key={j} className="text-xs text-white/50 flex items-center gap-1.5">
                    <EnvelopeSimple size={12} className="text-amber-400/70 shrink-0" />
                    {s.delay_days === 0 ? "Subito" : `Dopo ${s.delay_days} giorn${s.delay_days === 1 ? "o" : "i"}`}: <span className="text-white/75 truncate">{s.subject}</span>
                  </p>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Button data-testid={`automation-toggle-${a.id}`} onClick={() => handleToggle(a)} variant="ghost"
                  className="h-9 px-4 border border-white/15 text-white/70 hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
                  <Power size={13} className="mr-1" /> {a.active ? "Sospendi" : "Attiva"}
                </Button>
                <Button data-testid={`automation-edit-${a.id}`} onClick={() => openEdit(a)} variant="ghost"
                  className="h-9 px-4 border border-white/15 text-white/70 hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
                  Modifica
                </Button>
                <Button data-testid={`automation-delete-${a.id}`} onClick={() => handleDelete(a)} variant="ghost"
                  className="h-9 w-9 p-0 rounded-full text-red-400/60 hover:text-red-400 hover:bg-red-500/10">
                  <Trash size={15} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {queue.length > 0 && (
        <div className="mt-10 border border-surface-border rounded-lg bg-surface p-6 animate-fade-up" data-testid="automation-queue-card">
          <h3 className="font-heading text-lg font-semibold tracking-tight mb-4">Ultimi invii automatici</h3>
          <div className="space-y-2">
            {queue.map((q) => (
              <div key={q.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs border-b border-white/5 pb-2">
                <span className={`font-semibold shrink-0 ${STATUS_COLOR[q.status] || "text-white/50"}`}>{STATUS_LABEL[q.status] || q.status}</span>
                <span className="text-white/70 shrink-0">{q.email}</span>
                <span className="text-white/40 truncate">"{q.subject}"</span>
                <span className="text-white/35 ml-auto shrink-0">{q.status === "pending" ? `previsto ${new Date(q.due_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : (q.sent_at ? new Date(q.sent_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "")}</span>
                {q.error && <span className="text-red-400/80 truncate max-w-[280px]" title={q.error}>{q.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="automation-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
              <Lightning size={20} weight="duotone" className="text-amber-400" /> {editing?.id ? "Modifica sequenza" : "Nuova sequenza"}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Le email partono automaticamente per ogni nuovo contatto aggiunto alla lista, ai tempi impostati.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className={lbl}>Nome sequenza</Label>
                <Input required data-testid="automation-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Benvenuto" className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <Label className={lbl}>Lista contatti</Label>
                <Select value={form.list_id} onValueChange={(v) => setForm({ ...form, list_id: v })}>
                  <SelectTrigger data-testid="automation-list-select" className="h-10 bg-transparent border-white/10 text-white text-sm">
                    <SelectValue placeholder="Scegli la lista" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    {lists.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`automation-list-option-${l.id}`}>{l.name} ({(l.emails || []).length})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.steps.map((s, i) => (
              <div key={i} className="p-4 rounded-lg bg-white/[0.02] border border-white/10 space-y-3" data-testid={`automation-step-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-amber-300">Email {i + 1}</p>
                  {form.steps.length > 1 && (
                    <button type="button" data-testid={`automation-step-remove-${i}`} onClick={() => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }))}
                      className="text-red-400/60 hover:text-red-400"><Trash size={14} /></button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className={lbl}>Invia dopo (giorni)</Label>
                    <Input required type="number" min="0" max="365" data-testid={`automation-step-delay-${i}`} value={s.delay_days}
                      onChange={(e) => setStep(i, { delay_days: e.target.value })} className={inputCls} />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className={lbl}>Oggetto</Label>
                    <Input required data-testid={`automation-step-subject-${i}`} value={s.subject}
                      onChange={(e) => setStep(i, { subject: e.target.value })} placeholder="Benvenuto in AREAPAQ!" className={inputCls} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className={lbl}>Contenuto (HTML o testo)</Label>
                  <Textarea required data-testid={`automation-step-html-${i}`} value={s.html} rows={3}
                    onChange={(e) => setStep(i, { html: e.target.value })} placeholder="<p>Ciao! Grazie per esserti iscritto...</p>"
                    className="bg-transparent border-white/10 text-white text-sm placeholder:text-white/25 resize-y" />
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <Button type="button" data-testid="automation-add-step" variant="ghost" disabled={form.steps.length >= 10}
                onClick={() => setForm((f) => ({ ...f, steps: [...f.steps, { delay_days: 3, subject: "", html: "" }] }))}
                className="h-9 px-4 border border-white/15 text-white/70 hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
                <Plus size={13} className="mr-1" /> Aggiungi email
              </Button>
              <Button type="submit" disabled={saving || !form.list_id} data-testid="automation-save-button"
                className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm disabled:opacity-40">
                {saving ? "Salvataggio..." : "Salva sequenza"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
